import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { submitClientRequest, getIntakePrompts } from '../api';

// Bundled fallback if the admin prompts cannot be fetched.
const FALLBACK = [
  { key: 'audience', q: 'Who needs the training?', help: 'Roles or teams.' },
  { key: 'program_type', q: 'What program / state is involved?', help: 'e.g. LIHTC, Ohio' },
  { key: 'need', q: 'What problem are you trying to solve?', help: 'Describe it in your own words.' },
  { key: 'learner_count', q: 'How many learners may need access?', help: 'A rough number is fine.', type: 'number' },
  { key: 'urgency', q: 'Is this urgent or exploratory?', help: 'Any deadline, audit, or finding?' },
];

export default function RequestClass({ onDone }) {
  const qc = useQueryClient();
  const [steps, setSteps] = useState(FALLBACK);
  const [answers, setAnswers] = useState({});
  const [stage, setStage] = useState('exploring');

  useEffect(() => {
    getIntakePrompts()
      .then((res) => {
        const client = (res?.personas || []).find((p) => p.persona === 'client_casual');
        if (client?.steps?.length) setSteps(client.steps);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const submit = useMutation({
    mutationFn: () => submitClientRequest({
      training_need: answers.need || '',
      buyer_stage: stage,
      learner_count: answers.learner_count ? parseInt(answers.learner_count, 10) : null,
      intake_answers: answers,
    }),
    onSuccess: () => {
      toast.success('Request submitted');
      setAnswers({});
      qc.invalidateQueries({ queryKey: ['my-training-requests'] });
      onDone?.();
    },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });

  const field = 'w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800 mt-1';
  const canSubmit = (answers.need || '').trim().length > 0;

  return (
    <div className="max-w-[560px]">
      <p className="text-xs text-gray-500 mb-3">
        Tell us what training you need. Our team reviews and prepares it; you will see a price here when it is ready to buy.
      </p>
      {steps.map((s) => (
        <label key={s.key} className="text-xs text-gray-500 block mt-3">
          {s.q}{s.help && <span className="text-gray-400"> ({s.help})</span>}
          {s.type === 'number' ? (
            <input type="number" min="1" value={answers[s.key] || ''} onChange={(e) => set(s.key, e.target.value)} className={field} />
          ) : (
            <textarea value={answers[s.key] || ''} onChange={(e) => set(s.key, e.target.value)} rows={s.key === 'need' ? 4 : 2} className={field} />
          )}
        </label>
      ))}
      <label className="text-xs text-gray-500 block mt-3">Where are you in the process?
        <select value={stage} onChange={(e) => setStage(e.target.value)} className={field}>
          <option value="exploring">Just exploring</option>
          <option value="shopping">Comparing options</option>
          <option value="ready_to_schedule">Ready to schedule</option>
          <option value="ready_to_buy">Ready to buy</option>
        </select>
      </label>
      <button
        disabled={!canSubmit || submit.isPending}
        onClick={() => submit.mutate()}
        className="mt-4 text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40"
      >
        {submit.isPending ? 'Submitting...' : 'Submit request'}
      </button>
    </div>
  );
}
